import endpoints from "../../endpoints";
import { call, softFail, isSoft, ApiError, downloadCsv, qs } from "../audiences/api";
export { softFail, isSoft, ApiError };

/*
 * Sales "Now" rails (SALES_FEATURES_REVIEW_2026-10-05 §3 N1-N3): dealer
 * performance, pricing opportunities and the price scenario curve.
 *
 * Every call here is FAIL-SOFT: a 404 / 405 / 501 resolves to null (the
 * backend is landing these routes in parallel), and the views show a calm
 * "not available yet" line instead of an error page. Any other failure
 * still throws so real outages are visible.
 *
 * Ids travel as strings end to end (json-bigint parse in `call`).
 */

const ROOT = String(endpoints.product && endpoints.product.root || ""); // https://api.../v1
const idStr = v => v === undefined || v === null ? "" : String(v);
const enc = v => encodeURIComponent(idStr(v));

// ---- dealer performance (N1) -------------------------------------------------
// GET /v1/sales/dealers/{range}            → same shape as /v1/store/performance/{range}, keyed by dealer
// GET /v1/sales/dealers/{range}/{dealerId} → same shape as /v1/store/performance/{range}/{storeId}
// GET/PUT /v1/sales/dealers/targets, GET /v1/sales/dealers/targets/seed → mirror /v1/store/targets[/seed]

export const getDealerPerformance = (range, query) => softFail(call(`${ROOT}/sales/dealers/${enc(range)}`, {
  query
}));
export const getDealerDetail = (range, dealerId) => softFail(call(`${ROOT}/sales/dealers/${enc(range)}/${enc(dealerId)}`));
export const getDealerTargets = (from, to) => softFail(call(`${ROOT}/sales/dealers/targets`, {
  query: {
    from,
    to
  }
}));
export const seedDealerTargets = (from, to, growth) => softFail(call(`${ROOT}/sales/dealers/targets/seed`, {
  query: {
    from,
    to,
    growth
  }
}));
export const putDealerTargets = targets => call(`${ROOT}/sales/dealers/targets`, {
  method: "PUT",
  body: targets
});

// The roster payload is "the store shape keyed by dealer". Normalise the few
// names that can reasonably differ so the views read one vocabulary:
//   rows → d.dealers | d.stores | d.items | d.rows
//   id   → dealerId | storeId | accountNumber | id
export function dealerId(r) {
  if (!r) return "";
  return idStr(r.dealerId != null ? r.dealerId : r.storeId != null ? r.storeId : r.accountNumber != null ? r.accountNumber : r.id);
}
export function dealerRows(d) {
  if (!d) return [];
  const rows = d.dealers || d.stores || d.items || d.rows || [];
  return (Array.isArray(rows) ? rows : []).map(r => ({
    ...r,
    dealerId: dealerId(r),
    name: r.name || r.dealerName || r.accountName || dealerId(r)
  }));
}
export function dealerTotals(d) {
  const x = d || {};
  const pick = (...keys) => {
    for (const k of keys) if (x[k] != null) return x[k];
    return undefined;
  };
  return {
    revenue: pick("chainRevenue", "totalRevenue", "revenue", "totalAmount"),
    orders: pick("chainOrders", "totalOrders", "orders"),
    units: pick("chainUnits", "totalUnits", "units", "quantity"),
    aov: pick("chainAov", "aov"),
    attainmentPct: pick("chainAttainmentPct", "attainmentPct"),
    hasTargets: !!pick("hasTargets")
  };
}
// Targets grid rows: the store grid uses storeId; accept dealerId too and
// always expose dealerId for the cell keys and the PUT body.
export function targetRows(g) {
  return (g && g.rows || []).map(r => ({
    ...r,
    dealerId: dealerId(r)
  }));
}

// ---- pricing (N3) -----------------------------------------------------------
// GET /v1/sales/pricing/opportunities/{range}
//   → { items: [{ productId, title, imageUrl, views, addToCart?, units, revenue, price, suggestedPrice?, reason }] }
// GET /v1/sales/pricing/scenario/{productId}?price=
//   → { productId, currentPrice, points: [{ price, units, revenue }], lowSignal }

const n = v => {
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};
const first = (o, ...keys) => {
  for (const k of keys) if (o && o[k] !== undefined && o[k] !== null) return o[k];
  return undefined;
};

// Plain-words reason for a pricing opportunity row, from the rail's signals.
function pricingReason(r) {
  const conv = n(first(r, "conversionPer100Users", "ConversionPer100Users"));
  const cat = n(first(r, "categoryMedianConversionPer100Users", "CategoryMedianConversionPer100Users"));
  const pos = n(first(r, "pricePositionVsCategory", "PricePositionVsCategory"));
  if (first(r, "stockoutSuspect", "StockoutSuspect")) return "interest with no orders: stock-out or not orderable";
  const parts = [];
  if (pos != null && pos >= 1.15) parts.push(`priced ${Math.round((pos - 1) * 100)}% above its category`);else if (pos != null && pos <= 0.85) parts.push(`priced ${Math.round((1 - pos) * 100)}% below its category`);
  if (conv != null && cat != null && cat > 0 && conv < cat * 0.6) parts.push(`converts ${conv.toFixed(1)} per 100 visitors vs ${cat.toFixed(1)} for the category`);else if (conv != null && cat != null && cat > 0) parts.push(`converts ${conv.toFixed(1)} per 100 visitors (category ${cat.toFixed(1)})`);
  return parts.join(" · ") || "popular but under-ordered";
}
export async function getPricingOpportunities(range) {
  const d = await softFail(call(`${ROOT}/sales/pricing/opportunities/${enc(range)}`));
  if (d === null) return null;
  const items = Array.isArray(d) ? d : d && (d.items || d.Items || d.rows) || [];
  return items.map(r => ({
    productId: idStr(first(r, "productId", "ProductId", "id", "sku")),
    title: first(r, "title", "Title", "name") || "",
    imageUrl: first(r, "imageUrl", "ImageUrl", "imageLink", "ImageLink", "image") || "",
    views: n(first(r, "views", "Views")) || 0,
    addToCart: n(first(r, "addToCart", "AddToCart", "addToCarts", "carts")),
    units: n(first(r, "units", "Units", "unitsSold", "UnitsSold", "quantity")) || 0,
    revenue: n(first(r, "revenue", "Revenue", "totalAmount")) || 0,
    price: n(first(r, "price", "Price", "currentPrice", "realizedUnitPrice", "RealizedUnitPrice", "listPrice", "ListPrice")),
    categoryMedianPrice: n(first(r, "categoryMedianPrice", "CategoryMedianPrice")),
    suggestedPrice: n(first(r, "suggestedPrice", "SuggestedPrice")),
    // The rail carries the signals, not a sentence: conversion per 100 visitors against the category,
    // list price against the category median, and a stock-out suspicion. The reason is composed here.
    conversionPer100: n(first(r, "conversionPer100Users", "ConversionPer100Users")),
    categoryConversionPer100: n(first(r, "categoryMedianConversionPer100Users", "CategoryMedianConversionPer100Users")),
    pricePosition: n(first(r, "pricePositionVsCategory", "PricePositionVsCategory")),
    categoryMedianPrice: n(first(r, "categoryMedianPrice", "CategoryMedianPrice")),
    stockoutSuspect: !!first(r, "stockoutSuspect", "StockoutSuspect"),
    reason: first(r, "reason", "Reason") || pricingReason(r)
  })).map(row => ({
    ...row,
    revenue: row.revenue || (row.units && row.price ? Math.round(row.units * row.price * 100) / 100 : 0)
  }));
}
export async function getPricingScenario(productId, price, range) {
  const d = await softFail(call(`${ROOT}/sales/pricing/scenario/${enc(productId)}`, {
    query: {
      price: price != null ? price : undefined,
      range: range || undefined
    }
  }));
  if (d === null) return null;
  const pts = first(d, "points", "Points", "curve", "scenarios") || [];
  const points = (Array.isArray(pts) ? pts : []).map(p => ({
    price: n(first(p, "price", "Price")),
    units: n(first(p, "units", "Units", "quantity", "predictedUnits", "PredictedUnits")),
    revenue: n(first(p, "revenue", "Revenue", "totalAmount", "predictedRevenue", "PredictedRevenue")),
    label: first(p, "label", "Label") || "",
    pricePosition: n(first(p, "pricePosition", "PricePosition")),
    convPer100: n(first(p, "predictedConvPer100Users", "PredictedConvPer100Users"))
  })).filter(p => p.price != null).sort((a, b) => a.price - b.price);
  const curve = first(d, "tenantCurve", "TenantCurve") || {};
  const cluster = first(d, "cluster", "Cluster") || {};
  return {
    productId: idStr(first(d, "productId", "ProductId") || productId),
    currentPrice: n(first(d, "currentPrice", "CurrentPrice", "price")),
    points,
    lowSignal: !!(first(d, "lowSignal", "LowSignal") || first(curve, "lowSignal", "LowSignal")),
    // Too few of this product's own orders to estimate (units/revenue come back null).
    notEnoughSales: !!first(d, "notEnoughSales", "NotEnoughSales"),
    curveNote: first(curve, "note", "Note") || "",
    elasticity: n(first(curve, "elasticity", "Elasticity")),
    clusterMedianPrice: n(first(cluster, "medianPrice", "MedianPrice")),
    clusterSize: n(first(cluster, "size", "Size")),
    // When ?price= was asked, some shapes answer with the single point at the top level.
    at: n(first(d, "price", "Price")) != null && n(first(d, "units", "Units")) != null ? {
      price: n(first(d, "price", "Price")),
      units: n(first(d, "units", "Units")),
      revenue: n(first(d, "revenue", "Revenue"))
    } : null
  };
}

// ---- demand gaps (the Pricing tab renamed, 2026-10-06) --------------------------
// Same rail as getPricingOpportunities, read WITH its envelope and the fields the
// backend is adding in parallel (every one of them optional; absent = old rail):
//   GET /v1/sales/pricing/opportunities/{range}?rep=
//     → { items: [{ ..., priceSignal?, gap?, gapRank? }], siteAudience?: "consumer"|"trade", viewsScope?: "site"|... }
//   GET/PUT /v1/sales/pricing/settings → { siteAudience }
// `rep` scopes units to that rep's dealers; views stay site-wide when viewsScope says "site".
const bool = v => v === undefined || v === null || v === "" ? null : typeof v === "string" ? !/^(false|0|no)$/i.test(v) : !!v;
const AUDIENCES = ["consumer", "trade"];
export const normAudience = (v, fallback) => {
  const a = String(v || "").toLowerCase();
  return AUDIENCES.includes(a) ? a : fallback || "consumer";
};
export const audienceFromBusinessModel = bm => String(bm || "").toLowerCase() === "manufacturing" ? "trade" : "consumer";

// The composed reason, audience-aware: trade reads "ordered N per 100 trade visitors".
export function demandGapReason(r, audience) {
  const given = first(r, "reason", "Reason");
  const trade = audience === "trade";
  if (given) return trade ? String(given).replace(/converts\s+([\d.]+)\s+per 100 visitors/gi, "ordered $1 per 100 trade visitors") : String(given);
  const conv = n(first(r, "conversionPer100Users", "ConversionPer100Users"));
  const cat = n(first(r, "categoryMedianConversionPer100Users", "CategoryMedianConversionPer100Users"));
  const pos = n(first(r, "pricePositionVsCategory", "PricePositionVsCategory"));
  if (first(r, "stockoutSuspect", "StockoutSuspect")) return "interest with no orders: stock-out or not orderable";
  const verb = trade ? "ordered" : "converts";
  const who = trade ? "trade visitors" : "visitors";
  const parts = [];
  if (pos != null && pos >= 1.15) parts.push(`priced ${Math.round((pos - 1) * 100)}% above its category`);else if (pos != null && pos <= 0.85) parts.push(`priced ${Math.round((1 - pos) * 100)}% below its category`);
  if (conv != null && cat != null && cat > 0 && conv < cat * 0.6) parts.push(`${verb} ${conv.toFixed(1)} per 100 ${who} vs ${cat.toFixed(1)} for the category`);else if (conv != null && cat != null && cat > 0) parts.push(`${verb} ${conv.toFixed(1)} per 100 ${who} (category ${cat.toFixed(1)})`);
  return parts.join(" · ") || (trade ? "browsed by the trade, rarely ordered" : "popular but under-ordered");
}
function demandGapRow(r, audience) {
  const row = {
    productId: idStr(first(r, "productId", "ProductId", "id", "sku")),
    title: first(r, "title", "Title", "name") || "",
    imageUrl: first(r, "imageUrl", "ImageUrl", "imageLink", "ImageLink", "image") || "",
    views: n(first(r, "views", "Views")) || 0,
    addToCart: n(first(r, "addToCart", "AddToCart", "addToCarts", "carts")),
    units: n(first(r, "units", "Units", "unitsSold", "UnitsSold", "quantity")) || 0,
    revenue: n(first(r, "revenue", "Revenue", "totalAmount")) || 0,
    price: n(first(r, "price", "Price", "currentPrice", "realizedUnitPrice", "RealizedUnitPrice", "listPrice", "ListPrice")),
    suggestedPrice: n(first(r, "suggestedPrice", "SuggestedPrice")),
    conversionPer100: n(first(r, "conversionPer100Users", "ConversionPer100Users")),
    categoryConversionPer100: n(first(r, "categoryMedianConversionPer100Users", "CategoryMedianConversionPer100Users")),
    pricePosition: n(first(r, "pricePositionVsCategory", "PricePositionVsCategory")),
    categoryMedianPrice: n(first(r, "categoryMedianPrice", "CategoryMedianPrice")),
    stockoutSuspect: !!first(r, "stockoutSuspect", "StockoutSuspect"),
    // New with the demand-gaps rail; null = the field has not landed (keep the old behaviour).
    priceSignal: bool(first(r, "priceSignal", "PriceSignal")),
    gap: n(first(r, "gap", "Gap", "demandGap", "DemandGap")),
    gapRank: n(first(r, "gapRank", "GapRank")),
    reason: demandGapReason(r, audience)
  };
  return {
    ...row,
    revenue: row.revenue || (row.units && row.price ? Math.round(row.units * row.price * 100) / 100 : 0)
  };
}

/** Demand gaps with the envelope: { items, siteAudience, viewsScope, hasGap, hasPriceSignal } or null (rail absent). */
export async function getDemandGaps(range, {
  rep,
  audienceDefault
} = {}) {
  const d = await softFail(call(`${ROOT}/sales/pricing/opportunities/${enc(range)}`, {
    query: {
      rep: rep || undefined
    }
  }));
  if (d === null) return null;
  const siteAudience = normAudience(first(d, "siteAudience", "SiteAudience"), audienceDefault);
  const raw = Array.isArray(d) ? d : d && (d.items || d.Items || d.rows) || [];
  const items = raw.map(r => demandGapRow(r, siteAudience));
  const hasGap = items.some(r => r.gap != null);
  if (hasGap) items.sort((a, b) => (b.gap || 0) - (a.gap || 0));
  return {
    items,
    siteAudience,
    viewsScope: String(first(d, "viewsScope", "ViewsScope") || "").toLowerCase() || null,
    hasGap,
    hasPriceSignal: items.some(r => r.priceSignal != null)
  };
}
export const getPricingSettings = async () => {
  const d = await softFail(call(`${ROOT}/sales/pricing/settings`));
  if (d === null) return null;
  return {
    siteAudience: normAudience(first(d, "siteAudience", "SiteAudience"), null)
  };
};
export const putPricingSettings = siteAudience => call(`${ROOT}/sales/pricing/settings`, {
  method: "PUT",
  body: {
    siteAudience
  }
});

// ---- lapse risk (N2) ----------------------------------------------------------
// Rows from /v1/lifecycle/customers gain lapseRisk (healthy | watch | at_risk |
// lapsing), daysSinceOrder, expectedCadenceDays. Shared vocabulary for chips.
export const LAPSE_LEVELS = [
// Plain-language labels (cockpit wording, 2026-10-09): each dealer against how often it usually orders.
["healthy", "On track", "success"], ["watch", "Slowing", "info"], ["at_risk", "Overdue", "warning"], ["lapsing", "Stopped ordering", "danger"]];
export function lapseKey(v) {
  // The dealer rail sends lapseRisk as a string; the scorecard rail sends a health object { band, ... }.
  if (v && typeof v === "object") v = v.band || v.level || v.key || v.lapseRisk || "";
  if (v == null || typeof v === "boolean" || typeof v === "number") v = "";
  const s = String(v || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (s === "atrisk") return "at_risk";
  return LAPSE_LEVELS.some(([k]) => k === s) ? s : "";
}
export function lapseLabel(v) {
  const hit = LAPSE_LEVELS.find(([k]) => k === lapseKey(v));
  return hit ? hit[1] : "";
}
export function lapseTone(v) {
  const hit = LAPSE_LEVELS.find(([k]) => k === lapseKey(v));
  return hit ? hit[2] : "neutral";
}

// ============================================================================
// Sales executive view (SALES_EXECUTIVE_VIEW.md §7) + territory maps
// (SALES_TERRITORY_MAPS.md §5.1, §6.1). Same rules as above: every read is
// FAIL-SOFT (404 / 405 / 501 → null), ids are strings, and every shape is
// normalised here so the views read ONE vocabulary while the backend lands
// from the same spec in parallel.
// ============================================================================

const str = v => v === undefined || v === null ? "" : String(v);
const arr = v => Array.isArray(v) ? v : [];
// Ratios: the spec serves attainment / pace / shares as fractions (1.056); the
// store rail serves percents (105.6). Read both: an explicit *Pct key is /100.
const ratio = (o, key) => {
  if (!o) return null;
  const r = n(first(o, key, key[0].toUpperCase() + key.slice(1)));
  if (r != null) return r;
  const p = n(first(o, `${key}Pct`, `${key}Percent`));
  return p != null ? p / 100 : null;
};
const money = (o, ...keys) => n(first(o, ...keys));
export function cleanQuery(q) {
  const out = {};
  Object.entries(q || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "" && v !== false) out[k] = v;
  });
  return out;
}

// ---- normalisers -------------------------------------------------------------
// prev + delta: {prev:{revenue,orders}, delta:{revenue,orders}} per the spec, or
// the roster's prevTotalAmount / prevOrders; both land on prev.*.
function prevOf(r) {
  const p = r && r.prev || {};
  return {
    revenue: money(p, "revenue", "totalAmount") != null ? money(p, "revenue", "totalAmount") : money(r, "prevRevenue", "prevTotalAmount"),
    orders: n(first(p, "orders")) != null ? n(first(p, "orders")) : n(first(r, "prevOrders")),
    units: n(first(p, "units", "quantity")) != null ? n(first(p, "units", "quantity")) : n(first(r, "prevUnits", "prevQuantity"))
  };
}
function sparkOf(r) {
  const s = first(r, "sparkline", "Sparkline", "trend", "series");
  if (!Array.isArray(s)) return [];
  return s.map(v => v && typeof v === "object" ? n(first(v, "revenue", "totalAmount", "value", "v")) || 0 : n(v) || 0);
}
function healthOf(r) {
  const h = r && (r.health || r.Health) || null;
  if (!h) return null;
  if (typeof h === "string") return {
    band: lapseKey(h)
  };
  return {
    band: lapseKey(first(h, "band", "lapseRisk", "level")) || "",
    healthy: n(first(h, "healthy", "Healthy")) || 0,
    watch: n(first(h, "watch", "Watch")) || 0,
    atRisk: n(first(h, "atRisk", "at_risk", "AtRisk")) || 0,
    lapsing: n(first(h, "lapsing", "Lapsing")) || 0,
    revenueAtRisk: money(h, "revenueAtRisk", "revenueAtStake") || 0,
    ratio: n(first(h, "ratio")),
    cadenceDays: n(first(h, "cadenceDays", "expectedCadenceDays")),
    daysSinceOrder: n(first(h, "daysSinceOrder")),
    revenueAtStake: money(h, "revenueAtStake", "revenueAtRisk") || 0,
    expectedNextOrder: str(first(h, "expectedNextOrder", "nextOrderExpected")).slice(0, 10),
    lastOrder: str(first(h, "lastOrder", "lastOrderDate")).slice(0, 10)
  };
}
const statesOf = r => {
  const s = first(r, "states", "States", "regions");
  if (Array.isArray(s)) return s.map(x => str(x).trim().toUpperCase()).filter(Boolean);
  return str(s).split(",").map(x => x.trim().toUpperCase()).filter(Boolean);
};
export function repScorecard(r) {
  if (!r) return null;
  const revenue = money(r, "revenue", "totalAmount") || 0;
  const orders = n(first(r, "orders")) || 0;
  const prev = prevOf(r);
  const t = money(r, "target", "targetRevenue", "plan");
  return {
    kind: "rep",
    id: str(first(r, "repCode", "RepCode", "id")),
    repCode: str(first(r, "repCode", "RepCode", "id")),
    name: first(r, "name", "repName") || str(first(r, "repCode")),
    status: str(first(r, "status")).toLowerCase(),
    channel: first(r, "channel") || "",
    states: statesOf(r),
    zips: n(first(r, "zips", "postalCodes")) || 0,
    revenue,
    orders,
    units: n(first(r, "units", "quantity")) || 0,
    aov: n(first(r, "aov")) != null ? n(first(r, "aov")) : orders > 0 ? revenue / orders : null,
    prev,
    dealersAssigned: n(first(r, "dealersAssigned", "assignedAccounts")) || 0,
    dealersActive: n(first(r, "dealersActive", "customers")) || 0,
    newDealers: n(first(r, "newDealers")),
    target: t,
    attainment: t ? ratio(r, "attainment") : null,
    pace: t ? ratio(r, "pace") : null,
    onPlan: first(r, "onPlan"),
    health: healthOf(r),
    outOfTerritoryPct: ratio(r, "outOfTerritory") != null ? ratio(r, "outOfTerritory") : (() => {
      const o = n(first(r, "outOfTerritoryPct"));
      return o == null ? null : o > 1 ? o / 100 : o;
    })(),
    forecastNextQuarter: money(r, "forecastNextQuarter"),
    sparkline: sparkOf(r),
    bookChanged: !!first(r, "bookChanged"),
    raw: r
  };
}
export function dealerScorecard(r) {
  if (!r) return null;
  const revenue = money(r, "revenue", "totalAmount") || 0;
  const orders = n(first(r, "orders")) || 0;
  const ar = r.assignedRep || r.AssignedRep || null;
  const t = money(r, "target", "targetRevenue", "plan");
  const cats = arr(first(r, "categories", "categoryMix", "topCategories")).map(c => ({
    category: str(first(c, "category", "name")),
    share: (() => {
      const s = n(first(c, "share"));
      if (s != null) return s;
      const p = n(first(c, "sharePct"));
      return p != null ? p / 100 : null;
    })(),
    revenue: money(c, "revenue", "totalAmount"),
    productId: str(first(c, "productId", "topProductId"))
  }));
  return {
    kind: "dealer",
    id: str(first(r, "customerId", "accountNumber", "dealerId", "id")),
    customerId: str(first(r, "customerId", "accountNumber", "dealerId", "id")),
    accountNumber: str(first(r, "accountNumber", "customerId", "dealerId")),
    name: first(r, "name", "dealerName", "accountName") || str(first(r, "accountNumber", "customerId")),
    city: first(r, "city") || "",
    region: str(first(r, "region", "state")).toUpperCase(),
    channel: first(r, "channel") || "",
    stage: str(first(r, "stage", "lifecycleStage")),
    assignedRep: ar ? {
      repCode: str(first(ar, "repCode", "id")),
      name: first(ar, "name") || str(first(ar, "repCode"))
    } : first(r, "assignedRepId", "assignedRepCode") ? {
      repCode: str(first(r, "assignedRepId", "assignedRepCode")),
      name: first(r, "assignedRepName") || str(first(r, "assignedRepId"))
    } : null,
    sellingReps: n(first(r, "sellingReps", "reps")),
    revenue,
    orders,
    units: n(first(r, "units", "quantity")) || 0,
    aov: n(first(r, "aov")) != null ? n(first(r, "aov")) : orders > 0 ? revenue / orders : null,
    prev: prevOf(r),
    target: t,
    attainment: t ? ratio(r, "attainment") : null,
    pace: t ? ratio(r, "pace") : null,
    health: healthOf(r) || (lapseKey(first(r, "lapseRisk")) ? {
      band: lapseKey(first(r, "lapseRisk")),
      daysSinceOrder: n(first(r, "daysSinceOrder")),
      cadenceDays: n(first(r, "expectedCadenceDays")),
      revenueAtStake: 0
    } : null),
    categories: cats,
    firstOrder: str(first(r, "firstOrder", "firstOrderDate")).slice(0, 10),
    lastOrder: str(first(r, "lastOrder", "lastOrderDate")).slice(0, 10),
    sparkline: sparkOf(r),
    sparklineMonths: arr(first(r, "sparklineMonths", "months")).map(String),
    // Monthly trend with the plan beside it (dealer detail): [{ month, revenue, orders, plan }]
    trend: arr(first(r, "trend", "trendVsPlan")).filter(t => t && typeof t === "object").map(t => ({
      month: str(first(t, "month", "period", "date")),
      revenue: money(t, "revenue", "totalAmount") || 0,
      orders: n(first(t, "orders")) || 0,
      plan: money(t, "plan", "target")
    })),
    raw: r
  };
}
export function storeScorecard(r) {
  if (!r) return null;
  const revenue = money(r, "revenue", "totalAmount") || 0;
  const orders = n(first(r, "orders")) || 0;
  const t = money(r, "target", "targetRevenue", "plan");
  return {
    kind: "store",
    id: str(first(r, "storeId", "id")),
    storeId: str(first(r, "storeId", "id")),
    name: first(r, "name", "store") || str(first(r, "storeId")),
    type: str(first(r, "type")).toLowerCase(),
    region: str(first(r, "region")).toUpperCase(),
    revenue,
    orders,
    units: n(first(r, "units", "quantity")) || 0,
    aov: n(first(r, "aov")) != null ? n(first(r, "aov")) : orders > 0 ? revenue / orders : null,
    prev: {
      revenue: money(r, "revenuePrev", "prevRevenue", "prevTotalAmount"),
      orders: n(first(r, "ordersPrev", "prevOrders"))
    },
    target: t,
    attainment: t ? ratio(r, "attainment") : null,
    pace: t ? ratio(r, "pace") != null ? ratio(r, "pace") : ratio(r, "paceAttainment") : null,
    sharePct: n(first(r, "sharePct")),
    categories: arr(first(r, "categories", "topCategories")).map(c => ({
      category: str(first(c, "category", "name")),
      share: (() => {
        const s = n(first(c, "share"));
        if (s != null) return s;
        const p = n(first(c, "sharePct"));
        return p != null ? p / 100 : null;
      })()
    })),
    health: healthOf(r),
    sparkline: sparkOf(r),
    raw: r
  };
}

// List envelope → { items, totals, hasTargets, hasPerOrderRepData, bands, revenueAtRisk, source }
export function scorecardEnvelope(d, mapRow) {
  if (d === null || d === undefined) return null;
  const rows = Array.isArray(d) ? d : arr(first(d, "items", "Items", "rows", "reps", "dealers", "stores"));
  const env = Array.isArray(d) ? {} : d;
  const bandsRaw = env.bands || env.Bands || null;
  const bands = bandsRaw ? {
    healthy: n(first(bandsRaw, "healthy")) || 0,
    watch: n(first(bandsRaw, "watch")) || 0,
    at_risk: n(first(bandsRaw, "atRisk", "at_risk")) || 0,
    lapsing: n(first(bandsRaw, "lapsing")) || 0
  } : null;
  return {
    items: rows.map(mapRow).filter(Boolean),
    totals: env.totals || env.Totals || null,
    hasTargets: !!first(env, "hasTargets"),
    hasPerOrderRepData: first(env, "hasPerOrderRepData") !== false,
    bands,
    revenueAtRisk: money(env, "revenueAtRisk") || 0,
    total: n(first(env, "total", "totalCount")),
    source: "scorecards"
  };
}

// ---- reads (§7.1) ----------------------------------------------------------
export const getOverview = (range, query) => softFail(call(`${ROOT}/sales/overview/${enc(range)}`, {
  query: cleanQuery(query)
}));
export const getRepScorecards = async (range, query) => scorecardEnvelope(await softFail(call(`${ROOT}/sales/scorecards/reps/${enc(range)}`, {
  query: cleanQuery(query)
})), repScorecard);
export const getRepScorecard = async (range, repCode, query) => {
  const d = await softFail(call(`${ROOT}/sales/scorecards/reps/${enc(range)}/${enc(repCode)}`, {
    query: cleanQuery(query)
  }));
  return d ? repScorecard(d.item || d) : null;
};
export const getDealerScorecards = async (range, query) => scorecardEnvelope(await softFail(call(`${ROOT}/sales/scorecards/dealers/${enc(range)}`, {
  query: cleanQuery(query)
})), dealerScorecard);
export const getDealerScorecard = async (range, id, query) => {
  const d = await softFail(call(`${ROOT}/sales/scorecards/dealers/${enc(range)}/${enc(id)}`, {
    query: cleanQuery(query)
  }));
  return d ? dealerScorecard(d.item || d) : null;
};
export const getStoreScorecards = async (range, query) => scorecardEnvelope(await softFail(call(`${ROOT}/sales/scorecards/stores/${enc(range)}`, {
  query: cleanQuery(query)
})), storeScorecard);
export const getCoverage = (range, query) => softFail(call(`${ROOT}/sales/coverage/${enc(range)}`, {
  query: cleanQuery(query)
}));

// Overview ribbon: { totals, repsOnPlan, dealersAtRisk, uncoveredStates, outOfTerritoryPct, trendVsPlan }
export function overviewShape(d) {
  if (!d) return null;
  const t = d.totals || d.Totals || d;
  const rop = d.repsOnPlan || d.RepsOnPlan;
  const dar = d.dealersAtRisk || d.DealersAtRisk;
  const unc = d.uncoveredStates || d.UncoveredStates;
  const tvp = arr(first(d, "trendVsPlan", "trend"));
  return {
    revenue: money(t, "revenue", "totalAmount") || 0,
    prevRevenue: money(t, "prevRevenue", "prevTotalAmount"),
    orders: n(first(t, "orders")) || 0,
    prevOrders: n(first(t, "prevOrders")),
    units: n(first(t, "units", "quantity")) || 0,
    activeDealers: n(first(t, "activeDealers", "customers", "dealersActive")) || 0,
    prevActiveDealers: n(first(t, "prevActiveDealers", "prevCustomers")),
    newDealers: n(first(t, "newDealers")),
    prevNewDealers: n(first(t, "prevNewDealers")),
    target: money(t, "target", "plan", "targetRevenue"),
    attainment: ratio(t, "attainment"),
    pace: ratio(t, "pace"),
    hasTargets: !!first(d, "hasTargets") || money(t, "target", "plan", "targetRevenue") != null,
    repsOnPlan: rop && typeof rop === "object" ? {
      on: n(first(rop, "onPlan", "on", "count")) || 0,
      of: n(first(rop, "of", "total", "reps")) || 0
    } : rop != null ? {
      on: n(rop) || 0,
      of: n(first(d, "reps", "repCount")) || 0
    } : null,
    dealersAtRisk: dar && typeof dar === "object" ? {
      count: n(first(dar, "count", "dealers")) || 0,
      revenue: money(dar, "revenueAtRisk", "revenue", "revenueAtStake") || 0
    } : dar != null ? {
      count: n(dar) || 0,
      revenue: money(d, "revenueAtRisk") || 0
    } : null,
    // The top slowing dealers from the same whole-book scorer as dealersAtRisk (the tile), so the panel never contradicts it.
    dealersSlipping: arr(first(d, "dealersSlipping")).map(dealerScorecard),
    uncoveredStates: unc && typeof unc === "object" ? {
      count: n(first(unc, "count", "states")) || 0,
      revenue: money(unc, "revenue", "noRepRevenue") || 0
    } : unc != null ? {
      count: n(unc) || 0,
      revenue: money(d, "uncoveredRevenue") || 0
    } : null,
    outOfTerritoryPct: (() => {
      const o = n(first(d, "outOfTerritoryPct", "outOfTerritory"));
      return o == null ? null : o > 1 ? o / 100 : o;
    })(),
    hasPerOrderRepData: first(d, "hasPerOrderRepData") !== false,
    trendVsPlan: tvp.map(p => ({
      period: str(first(p, "period", "month", "date")),
      actual: money(p, "actual", "revenue", "totalAmount") || 0,
      plan: money(p, "plan", "target")
    })),
    channels: arr(first(d, "channels")).map(c => ({
      channel: str(first(c, "channel")),
      revenue: money(c, "revenue", "totalAmount") || 0,
      reps: n(first(c, "reps")) || 0,
      share: ratio(c, "share")
    })),
    dataNotes: arr(first(d, "dataNotes", "notes")).map(String),
    raw: d
  };
}
export function coverageShape(d) {
  if (!d) return null;
  const states = arr(Array.isArray(d) ? d : first(d, "states", "items", "rows")).map(s => ({
    region: str(first(s, "region", "state", "code")).toUpperCase(),
    reps: arr(first(s, "reps", "coveringReps")).map(r => typeof r === "object" ? {
      repCode: str(first(r, "repCode", "id")),
      name: first(r, "name") || str(first(r, "repCode"))
    } : {
      repCode: str(r),
      name: str(r)
    }),
    dealers: n(first(s, "dealers", "totalDealers")) || 0,
    activeDealers: n(first(s, "activeDealers", "dealersActive")) || 0,
    revenue: money(s, "revenue", "totalAmount") || 0,
    noRepRevenue: money(s, "noRepRevenue") || 0,
    outOfTerritoryRevenue: money(s, "outOfTerritoryRevenue") || 0,
    flags: arr(first(s, "flags")).map(f => str(f).toLowerCase().replace(/_/g, " "))
  }));
  const sum = Array.isArray(d) ? {} : d.summary || d.Summary || {};
  return {
    states,
    summary: {
      covered: n(first(sum, "covered")) != null ? n(first(sum, "covered")) : states.filter(s => s.reps.length).length,
      uncovered: n(first(sum, "uncovered")) != null ? n(first(sum, "uncovered")) : states.filter(s => s.flags.includes("uncovered")).length,
      uncoveredRevenue: money(sum, "uncoveredRevenue") != null ? money(sum, "uncoveredRevenue") : states.filter(s => s.flags.includes("uncovered")).reduce((a, s) => a + s.revenue, 0),
      overlap: n(first(sum, "overlap", "overlaps")) != null ? n(first(sum, "overlap", "overlaps")) : states.filter(s => s.flags.includes("overlap")).length,
      overlapStates: states.filter(s => s.flags.includes("overlap")).map(s => s.region),
      noActivity: n(first(sum, "noActivity")) != null ? n(first(sum, "noActivity")) : states.filter(s => s.flags.includes("no activity")).length,
      outOfTerritoryRevenue: money(sum, "outOfTerritoryRevenue") != null ? money(sum, "outOfTerritoryRevenue") : states.reduce((a, s) => a + s.outOfTerritoryRevenue, 0),
      noRepRevenue: money(sum, "noRepRevenue") != null ? money(sum, "noRepRevenue") : states.reduce((a, s) => a + s.noRepRevenue, 0),
      revenue: money(sum, "revenue") != null ? money(sum, "revenue") : states.reduce((a, s) => a + s.revenue, 0)
    },
    raw: d
  };
}

// ---- roster fallbacks ---------------------------------------------------------
// Until /scorecards/* is deployed the cockpit still reads the rails that exist
// (one row per rep / dealer from the rollups; nothing is summed client-side).
// The envelope says source:"roster" so the view can say which columns are
// pending (targets, health, sparkline, new dealers).
export async function getRepScorecardsOrRoster(range, query) {
  const sc = await getRepScorecards(range, query);
  if (sc) return sc;
  const q = cleanQuery({
    comparison: query && query.comparison,
    customerType: query && query.customerType,
    status: query && query.status,
    region: query && query.region
  });
  const rows = await softFail(call(`${ROOT}/sales/reps/${enc(range)}`, {
    query: q
  }));
  if (rows === null) return null;
  const env = scorecardEnvelope(arr(rows), repScorecard);
  env.source = "roster";
  env.hasTargets = false;
  return env;
}
export async function getDealerScorecardsOrRoster(range, query) {
  const sc = await getDealerScorecards(range, query);
  if (sc) return sc;
  const q = cleanQuery({
    comparison: query && query.comparison,
    customerType: query && query.customerType,
    status: query && query.status,
    region: query && query.region,
    rep: query && query.rep,
    top: query && query.top || 200
  });
  const rows = await softFail(call(`${ROOT}/sales/customers/${enc(range)}`, {
    query: q
  }));
  if (rows === null) return null;
  const env = scorecardEnvelope(arr(rows), dealerScorecard);
  env.source = "roster";
  env.hasTargets = false;
  return env;
}
export async function getStoreScorecardsOrRoster(range, query) {
  const sc = await getStoreScorecards(range, query);
  if (sc) return sc;
  const d = await softFail(call(`${ROOT}/store/performance/${enc(range)}`, {
    query: cleanQuery(query)
  }));
  if (d === null) return null;
  const env = scorecardEnvelope({
    items: arr(d.stores || d.items),
    hasTargets: !!d.hasTargets,
    totals: d
  }, storeScorecard);
  env.source = "roster";
  return env;
}
export async function getOverviewOrTotals(range, query) {
  const o = await getOverview(range, query);
  if (o) return overviewShape(o);
  const q = cleanQuery({
    comparison: query && query.comparison,
    customerType: query && query.customerType,
    status: query && query.status
  });
  const [t, a] = await Promise.all([softFail(call(`${ROOT}/sales/totals/${enc(range)}`, {
    query: q
  })), softFail(call(`${ROOT}/sales/attribution/${enc(range)}`, {
    query: cleanQuery({
      status: query && query.status
    })
  })).catch(() => null)]);
  if (t === null) return null;
  const totals = Array.isArray(t) ? t[0] || {} : t;
  const shape = overviewShape({
    totals,
    hasPerOrderRepData: a ? a.hasPerOrderRepData !== false : false,
    outOfTerritoryPct: a && a.mismatchPct != null ? Number(a.mismatchPct) / 100 : null
  });
  shape.source = "roster";
  return shape;
}
export async function getCoverageOrTerritories(range, query) {
  const c = await getCoverage(range, query);
  if (c) return coverageShape(c);
  const q = cleanQuery({
    customerType: query && query.customerType,
    status: query && query.status
  });
  const [terr, reps] = await Promise.all([softFail(call(`${ROOT}/sales/territories/${enc(range)}`, {
    query: q
  })), softFail(call(`${ROOT}/sales/reps/${enc(range)}`, {
    query: q
  })).catch(() => [])]);
  if (terr === null) return null;
  const byState = {};
  arr(reps).forEach(r => statesOf(r).forEach(s => {
    (byState[s] = byState[s] || []).push({
      repCode: str(r.repCode),
      name: r.name || str(r.repCode)
    });
  }));
  const rows = arr(terr).map(t => {
    const region = str(t.region).toUpperCase();
    const covering = byState[region] || [];
    const flags = [];
    if (!covering.length && (n(t.totalDealers) > 0 || n(t.totalAmount) > 0)) flags.push("uncovered");
    if (covering.length && !(n(t.orders) > 0)) flags.push("no activity");
    return {
      region,
      reps: covering,
      dealers: n(t.totalDealers) || 0,
      activeDealers: n(t.activeDealers) || 0,
      revenue: n(t.totalAmount) || 0,
      noRepRevenue: covering.length ? 0 : n(t.totalAmount) || 0,
      outOfTerritoryRevenue: 0,
      flags
    };
  });
  const shape = coverageShape({
    states: rows
  });
  shape.source = "roster";
  return shape;
}

// ---- targets (§7.2): one sales_target table read through one service --------
// GET  /v1/sales/targets?scope=rep|dealer|territory&from=&to=&ids=
// GET  /v1/sales/targets/seed?scope=&from=&to=&growth=
// PUT  /v1/sales/targets { scope, targets: [{ scopeId, period, revenueGoal, ordersGoal?, unitsGoal?, newDealersGoal? }] }
// DELETE /v1/sales/targets?scope=&scopeId=&period=
// GET  /v1/sales/targets/template.csv?scope=&from=&to=
export const getSalesTargets = (scope, from, to, ids) => softFail(call(`${ROOT}/sales/targets`, {
  query: cleanQuery({
    scope,
    from,
    to,
    ids
  })
}));
export const seedSalesTargets = (scope, from, to, growth) => softFail(call(`${ROOT}/sales/targets/seed`, {
  query: cleanQuery({
    scope,
    from,
    to,
    growth
  })
}));
export const putSalesTargets = (scope, targets) => call(`${ROOT}/sales/targets`, {
  method: "PUT",
  body: {
    scope,
    targets
  }
});
export const deleteSalesTarget = (scope, scopeId, period) => call(`${ROOT}/sales/targets`, {
  method: "DELETE",
  query: {
    scope,
    scopeId,
    period
  }
});
export const downloadSalesTargetsTemplate = (scope, from, to) => downloadCsv(`${ROOT}/sales/targets/template.csv${qs({
  scope,
  from,
  to
})}`, `sales-targets-${scope}-${from}-${to}.csv`);
// Grid rows → { scopeId, name, sub, cells }; the store/dealer grids keyed rows on storeId / dealerId.
export function salesTargetRows(g) {
  return arr(g && (g.rows || g.items)).map(r => ({
    ...r,
    scopeId: str(first(r, "scopeId", "repCode", "dealerId", "customerId", "storeId", "id")),
    name: first(r, "name") || str(first(r, "scopeId", "repCode", "dealerId")),
    sub: first(r, "repCode", "accountNumber", "assignedRepName", "channel") || "",
    lastYear: money(r, "lastYearActual", "lastYear", "priorActual"),
    bookChanged: !!first(r, "bookChanged"),
    cells: r.cells || {}
  }));
}

// ---- exports (§7.3): every list route accepts format=csv -------------------
export const exportScorecardsCsv = (kind, range, query) => downloadCsv(`${ROOT}/sales/scorecards/${enc(kind)}/${enc(range)}${qs({
  ...cleanQuery(query),
  format: "csv"
})}`, `sales-${kind}-${range}.csv`);
export const exportCoverageCsv = (range, query) => downloadCsv(`${ROOT}/sales/coverage/${enc(range)}${qs({
  ...cleanQuery(query),
  format: "csv"
})}`, `sales-coverage-${range}.csv`);

// ---- geo (SALES_TERRITORY_MAPS.md §6.1) -----------------------------------------
// GeoJSON FeatureCollection → [{ lat, lng, ...properties }] (also accepts a plain list with lat/lng).
export function geoPoints(d) {
  if (!d) return [];
  const feats = Array.isArray(d) ? d : arr(first(d, "features", "items", "points"));
  return feats.map(f => {
    const p = f.properties || f;
    let lat = n(first(f, "lat", "latitude")),
      lng = n(first(f, "lng", "lon", "longitude"));
    const g = f.geometry;
    if ((lat == null || lng == null) && g && Array.isArray(g.coordinates)) {
      lng = n(g.coordinates[0]);
      lat = n(g.coordinates[1]);
    }
    if (lat == null || lng == null) return null;
    return {
      ...p,
      lat,
      lng,
      id: str(first(p, "customerId", "storeId", "id", "accountNumber")),
      band: lapseKey(first(p, "band", "lapseRisk")),
      revenue: money(p, "revenue", "totalAmount") || 0,
      geoSource: str(first(p, "geoSource", "geo_source"))
    };
  }).filter(Boolean);
}
export const getGeoDealers = async (range, query) => {
  const d = await softFail(call(`${ROOT}/sales/geo/dealers/${enc(range)}`, {
    query: cleanQuery({
      top: 5000,
      ...query
    })
  }));
  return d === null ? null : geoPoints(d);
};
export const getGeoStores = async (range, query) => {
  const d = await softFail(call(`${ROOT}/sales/geo/stores/${enc(range)}`, {
    query: cleanQuery(query)
  }));
  return d === null ? null : geoPoints(d);
};
export const getGeoSummary = (range, query) => softFail(call(`${ROOT}/sales/geo/summary/${enc(range)}`, {
  query: cleanQuery(query)
}));
// Coverage: { hexes: [{ h3, boundary|polygon: [[lat,lng]...] | geometry, lat, lng, reps: [{repCode, zips}], uncoveredZips, overlap }], legend: [{repCode,name,color,zips}], summary }
export async function getGeoCoverage(query) {
  const d = await softFail(call(`${ROOT}/sales/geo/coverage`, {
    query: cleanQuery({
      grain: "hex5",
      ...query
    })
  }));
  if (d === null) return null;
  const hexes = arr(first(d, "hexes", "items", "features")).map(h => {
    const p = h.properties || h;
    let ring = first(h, "boundary", "polygon", "ring");
    const g = h.geometry;
    if (!ring && g && g.type === "Polygon" && Array.isArray(g.coordinates)) ring = (g.coordinates[0] || []).map(([lng, lat]) => [lat, lng]);
    if (Array.isArray(ring)) ring = ring.map(pt => Array.isArray(pt) ? [n(pt[0]), n(pt[1])] : [n(pt.lat), n(pt.lng)]).filter(pt => pt[0] != null && pt[1] != null);
    const reps = arr(first(p, "reps", "owners")).map(r => typeof r === "object" ? {
      repCode: str(first(r, "repCode", "id")),
      name: first(r, "name") || "",
      zips: n(first(r, "zips")) || 0
    } : {
      repCode: str(r),
      name: "",
      zips: 0
    });
    return {
      h3: str(first(p, "h3", "id")),
      ring: ring || null,
      lat: n(first(p, "lat")),
      lng: n(first(p, "lng", "lon")),
      reps,
      uncoveredZips: n(first(p, "uncoveredZips", "uncovered")) || 0,
      zips: n(first(p, "zips")) || 0,
      state: str(first(p, "state", "region")).toUpperCase(),
      overlap: !!first(p, "overlap"),
      zipList: arr(first(p, "zipList", "zipCodes")).map(String)
    };
  });
  const legend = arr(first(d, "legend", "reps")).map(l => ({
    repCode: str(first(l, "repCode", "id")),
    name: first(l, "name") || str(first(l, "repCode")),
    color: first(l, "color") || "",
    zips: n(first(l, "zips")) || 0
  }));
  return {
    hexes,
    legend,
    summary: d.summary || d.Summary || null,
    raw: d
  };
}
export async function getGeoHeat(range, metric, channel) {
  const cells = await getGeoHeatCells(range, metric, channel);
  if (cells === null) return null;
  const out = cells.filter(z => z.lat != null && z.lng != null);
  out.bookedAvailable = cells.bookedAvailable;
  return out;
}
// Every ZIP3 cell, coordinates or not (the dealer page looks one up by prefix).
export async function getGeoHeatCells(range, metric, channel) {
  const d = await softFail(call(`${ROOT}/sales/geo/heat/${enc(range)}`, {
    query: cleanQuery({
      metric,
      channel
    })
  }));
  if (d === null) return null;
  const cells = arr(Array.isArray(d) ? d : first(d, "cells", "items", "rows", "zip3s")).map(z => ({
    zip3: str(first(z, "zip3", "id")),
    lat: n(first(z, "lat")),
    lng: n(first(z, "lng", "lon")),
    label: first(z, "label") || str(first(z, "zip3")),
    state: str(first(z, "state", "region")).toUpperCase(),
    demand: n(first(z, "demand")) || 0,
    demandIndex: n(first(z, "demandIndex")) || 0,
    booked: money(z, "booked", "revenue") || 0,
    bookedIndex: n(first(z, "bookedIndex")) || 0,
    gap: n(first(z, "gap")) != null ? n(first(z, "gap")) : (n(first(z, "demandIndex")) || 0) - (n(first(z, "bookedIndex")) || 0),
    dealers: n(first(z, "dealers")) || 0,
    // `reps` is a count on the heat rail (reps assigned in the ZIP3); a list when a rail sends one
    reps: Array.isArray(first(z, "reps")) ? arr(first(z, "reps")).map(r => typeof r === "object" ? str(first(r, "repCode", "name")) : str(r)) : [],
    repCount: Array.isArray(first(z, "reps")) ? arr(first(z, "reps")).length : n(first(z, "reps", "repCount")) || 0,
    uncovered: !!first(z, "uncovered") || (Array.isArray(first(z, "reps")) ? !arr(first(z, "reps")).length : !(n(first(z, "reps", "repCount")) > 0))
  }));
  // false = no order in the window could be placed on a ZIP (no customer postal codes on file):
  // "no orders" there means "not known". Older services do not send it (undefined = assume known).
  const ba = Array.isArray(d) ? undefined : first(d, "bookedAvailable");
  cells.bookedAvailable = ba === undefined || ba === null ? true : !!ba;
  return cells;
}
export const getPostalOwner = (zip, channel) => softFail(call(`${ROOT}/sales/postal/owner/${enc(zip)}`, {
  query: cleanQuery({
    channel
  })
}));

// ---- territories upload (§5.1): dry run → report → apply / rollback -------------
// POST /v1/sales/territories/upload (multipart: file, kind, mode) → { id, kind, mode, rows, report: { issues[], summary }, status }
// POST /v1/sales/territories/upload/{id}/apply   → { applied }
// POST /v1/sales/territories/upload/{id}/rollback → { rolledBack }
// GET  /v1/sales/territories/uploads             → [{ id, kind, mode, fileName, rows, applied, status, createdBy, createdDate, appliedDate }]
export function uploadReport(d) {
  if (!d) return null;
  const rep = d.report || d.Report || d.validation || d;
  const issues = arr(first(rep, "issues", "items", "messages", "checks")).map(i => ({
    severity: str(first(i, "severity", "level")).toLowerCase() || "info",
    check: str(first(i, "check", "code", "kind")),
    message: first(i, "message", "text") || "",
    detail: first(i, "detail", "hint") || "",
    row: n(first(i, "row", "line")),
    count: n(first(i, "count")),
    suggestion: first(i, "suggestion", "didYouMean") || "",
    repCode: str(first(i, "repCode")),
    zip: str(first(i, "zip", "postalCode")),
    download: first(i, "downloadUrl", "download") || ""
  }));
  const sum = rep.summary || rep.Summary || {};
  return {
    id: str(first(d, "id", "uploadId")),
    kind: str(first(d, "kind")),
    mode: str(first(d, "mode")),
    fileName: first(d, "fileName") || "",
    rows: n(first(d, "rows", "rowCount")) || 0,
    status: str(first(d, "status")).toLowerCase() || "dry_run",
    issues,
    blocking: issues.filter(i => i.severity === "block").length,
    summary: {
      reps: n(first(sum, "reps")) || 0,
      zips: n(first(sum, "zips")) || 0,
      channels: arr(first(sum, "channels")).map(String),
      applicable: n(first(sum, "applicable", "applyRows"))
    },
    raw: d
  };
}
export async function uploadTerritories(file, {
  kind = "zip_rep",
  mode = "merge",
  text
} = {}) {
  const form = new FormData();
  if (file) form.append("file", file, file.name || "territories.csv");else if (text != null) form.append("file", new Blob([text], {
    type: "text/csv"
  }), "territories.csv");
  form.append("kind", kind);
  form.append("mode", mode);
  form.append("dryRun", "true");
  const d = await softFail(call(`${ROOT}/sales/territories/upload`, {
    method: "POST",
    form
  }));
  return d === null ? null : uploadReport(d);
}
export const applyTerritoryUpload = (id, mappings) => call(`${ROOT}/sales/territories/upload/${enc(id)}/apply`, {
  method: "POST",
  body: mappings ? {
    mappings
  } : {}
});
export const rollbackTerritoryUpload = id => call(`${ROOT}/sales/territories/upload/${enc(id)}/rollback`, {
  method: "POST",
  body: {}
});
export const listTerritoryUploads = async () => {
  const d = await softFail(call(`${ROOT}/sales/territories/uploads`));
  return d === null ? null : arr(Array.isArray(d) ? d : first(d, "items", "uploads")).map(u => ({
    ...uploadReport(u),
    createdBy: u.createdBy || "",
    createdDate: u.createdDate || "",
    appliedDate: u.appliedDate || "",
    applied: n(u.applied) || 0
  }));
};

// ============================================================================
// Dealer detail rails (dealer page upgrade): profile + contacts, lifetime,
// timeline, notes, co-purchase. Same rules: FAIL-SOFT reads (404 / 405 / 501
// resolve to null; the page hides the card or shows a calm line), ids as
// strings, one vocabulary per shape.
// ============================================================================

// GET /v1/sales/dealers/{range}/{accountNumber}
//   → { profile{ accountNumber, name, email, phone, billTo, shipTo, stage, lapsed }, assignedRep{ repCode, name, email, phone },
//       contacts[{ firstName, lastName, email, phone, role, isPrimary, personId }] }
const contactOf = c => ({
  personId: str(first(c, "personId", "id")),
  firstName: first(c, "firstName") || "",
  lastName: first(c, "lastName") || "",
  name: [first(c, "firstName"), first(c, "lastName")].filter(Boolean).join(" ") || first(c, "name") || first(c, "email") || "",
  email: first(c, "email") || "",
  phone: first(c, "phone") || "",
  role: first(c, "role", "title") || "",
  isPrimary: !!first(c, "isPrimary", "primary")
});
export async function getDealerProfile(range, accountNumber) {
  const d = await getDealerDetail(range, accountNumber);
  if (d === null) return null;
  const p = d.profile || d.Profile || d;
  const ar = d.assignedRep || d.AssignedRep || p.assignedRep || null;
  return {
    profile: {
      accountNumber: str(first(p, "accountNumber", "customerId", "dealerId", "id")),
      name: first(p, "name", "dealerName", "accountName") || "",
      email: first(p, "email") || "",
      phone: first(p, "phone") || "",
      billTo: first(p, "billTo", "billingAddress") || null,
      shipTo: first(p, "shipTo", "shippingAddress") || null,
      stage: str(first(p, "stage", "lifecycleStage")),
      lapsed: !!first(p, "lapsed")
    },
    assignedRep: ar ? {
      repCode: str(first(ar, "repCode", "id")),
      name: first(ar, "name") || str(first(ar, "repCode")),
      email: first(ar, "email") || "",
      phone: first(ar, "phone") || ""
    } : null,
    // Primary contact first, then the rest in feed order.
    contacts: arr(first(d, "contacts", "Contacts")).map(contactOf).sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0)),
    raw: d
  };
}

// GET /v1/sales/dealers/lifetime/{accountNumber} → { firstOrder, lastOrder, orders, units, revenue, avgGapDays, avgOrderValue, yearsActive }
export async function getDealerLifetime(accountNumber) {
  const d = await softFail(call(`${ROOT}/sales/dealers/lifetime/${enc(accountNumber)}`));
  if (d === null) return null;
  const x = d.item || d.lifetime || d;
  const revenue = money(x, "revenue", "totalAmount") || 0;
  const orders = n(first(x, "orders")) || 0;
  return {
    firstOrder: str(first(x, "firstOrder", "firstOrderDate")).slice(0, 10),
    lastOrder: str(first(x, "lastOrder", "lastOrderDate")).slice(0, 10),
    orders,
    revenue,
    units: n(first(x, "units", "quantity")) || 0,
    avgGapDays: n(first(x, "avgGapDays", "averageGapDays")),
    avgOrderValue: n(first(x, "avgOrderValue", "aov")) != null ? n(first(x, "avgOrderValue", "aov")) : orders > 0 ? revenue / orders : null,
    yearsActive: n(first(x, "yearsActive")),
    raw: x
  };
}

// GET /v1/sales/dealers/timeline/{accountNumber}?range=&top=30
//   → { items[{ at, kind (order|quote|order_request|note|journey), title, detail, ref{ type, id } }] } newest first
export async function getDealerTimeline(accountNumber, range, top = 30) {
  const d = await softFail(call(`${ROOT}/sales/dealers/timeline/${enc(accountNumber)}`, {
    query: cleanQuery({
      range,
      top
    })
  }));
  if (d === null) return null;
  return arr(Array.isArray(d) ? d : first(d, "items", "Items", "events", "rows")).map(i => {
    const ref = i.ref || i.Ref || null;
    return {
      at: str(first(i, "at", "date", "createdDate", "when")),
      kind: str(first(i, "kind", "type")).toLowerCase().replace(/[\s-]+/g, "_"),
      title: first(i, "title", "summary") || "",
      detail: first(i, "detail", "body", "note") || "",
      ref: ref ? {
        type: str(first(ref, "type", "kind")).toLowerCase().replace(/[\s-]+/g, "_"),
        id: str(first(ref, "id"))
      } : null
    };
  }).sort((a, b) => b.at > a.at ? 1 : b.at < a.at ? -1 : 0);
}

// Notes: GET/POST /v1/sales/dealers/notes/{accountNumber}, PUT/DELETE .../{id}
//   → { items[{ id, body, createdBy, createdDate }] }
const noteOf = x => ({
  id: str(first(x, "id", "noteId")),
  body: first(x, "body", "text", "note") || "",
  createdBy: first(x, "createdBy", "author", "by") || "",
  createdDate: str(first(x, "createdDate", "at", "created")),
  modifiedDate: str(first(x, "modifiedDate", "updatedDate"))
});
const unwrapNote = d => d && typeof d === "object" ? noteOf(d.item || d.note || d) : null;
export async function getDealerNotes(accountNumber) {
  const d = await softFail(call(`${ROOT}/sales/dealers/notes/${enc(accountNumber)}`));
  if (d === null) return null;
  return arr(Array.isArray(d) ? d : first(d, "items", "Items", "notes")).map(noteOf).sort((a, b) => b.createdDate > a.createdDate ? 1 : b.createdDate < a.createdDate ? -1 : 0);
}
export async function addDealerNote(accountNumber, body) {
  const d = await softFail(call(`${ROOT}/sales/dealers/notes/${enc(accountNumber)}`, {
    method: "POST",
    body: {
      body
    }
  }));
  return d === null ? null : unwrapNote(d) || {
    id: "",
    body,
    createdBy: "",
    createdDate: new Date().toISOString()
  };
}
export async function updateDealerNote(accountNumber, id, body) {
  const d = await softFail(call(`${ROOT}/sales/dealers/notes/${enc(accountNumber)}/${enc(id)}`, {
    method: "PUT",
    body: {
      body
    }
  }));
  return d === null ? null : unwrapNote(d) || {
    id,
    body
  };
}
// Resolves true when deleted, null when the rail is not there.
export async function deleteDealerNote(accountNumber, id) {
  const d = await softFail(call(`${ROOT}/sales/dealers/notes/${enc(accountNumber)}/${enc(id)}`, {
    method: "DELETE"
  }));
  return d === null ? null : true;
}

// Co-purchase (AnalyticsService SalesController `copurchase`): PRODUCT-keyed.
// GET /v1/sales/copurchase?product={productId}&limit=8 → { product, count, items[{ id, title, category, score }] }
export async function getCoPurchase(productId, limit = 8) {
  const d = await softFail(call(`${ROOT}/sales/copurchase`, {
    query: cleanQuery({
      product: productId,
      limit
    })
  }));
  if (d === null) return null;
  return arr(Array.isArray(d) ? d : first(d, "items", "Items", "neighbors")).map(r => ({
    productId: str(first(r, "id", "productId", "neighborId", "neighbor_id")),
    title: first(r, "title", "name") || str(first(r, "id", "productId", "neighborId")),
    sku: str(first(r, "sku", "Sku")),
    category: first(r, "category", "productType") || "",
    score: n(first(r, "score")) || 0,
    imageUrl: first(r, "imageUrl", "imageLink", "image_link", "image") || ""
  }));
}
// "Peers buy, this account doesn't": the rail is product-keyed, so ask it for
// each of the account's top products, merge by best score and drop everything
// the account already buys. null when none of the lookups answered.
export async function getPeerProducts(topProducts, boughtIds, limit = 8) {
  const srcs = arr(topProducts).filter(p => p && p.productId).slice(0, 3);
  if (!srcs.length) return null;
  const lists = await Promise.all(srcs.map(p => getCoPurchase(str(p.productId), limit).catch(() => null)));
  if (lists.every(l => l === null)) return null;
  const bought = new Set(arr(boughtIds).map(str));
  const best = new Map();
  lists.forEach((l, i) => arr(l).forEach(r => {
    if (!r.productId || bought.has(r.productId)) return;
    const cur = best.get(r.productId);
    if (!cur || cur.score < r.score) best.set(r.productId, {
      ...r,
      because: srcs[i].title || srcs[i].productId
    });
  }));
  return Array.from(best.values()).sort((a, b) => b.score - a.score).slice(0, limit);
}

// ---- rep drill-downs (Territory & Reps redesign 2026-10-09, boards 6-7) ------
// GET /v1/sales/reps/{range}/{repCode}/dealer-products?dealer=&category=
//   → { dealers: [{ dealerId, name, city, region, revenue, productCount }],
//       dealer: { dealerId, name, city, region, revenue, revenuePrev, productCount, lastOrder } | null,
//       products: [{ sku, title, category, image, orders, units, revenue, revenuePrev, deltaPct }],
//       stoppedBuying: [{ sku, title, revenuePrev }] }
// GET /v1/sales/reps/{range}/{repCode}/gaps?compare=all|{repCode}&category=&series=&minDealers=
//   → { repDealers, compareDealers, summary: { bigGaps, dealersToCall, leads },
//       items: [{ sku, title, category, series, image, repShare, otherShare, gapPts,
//                 missingCount, missingDealers: [{ dealerId, name, city, region, lastOrder }] }] }
export const getRepDealerProducts = (range, repCode, query) => softFail(call(`${ROOT}/sales/reps/${enc(range)}/${enc(repCode)}/dealer-products`, {
  query
}));
export const getRepGaps = (range, repCode, query) => softFail(call(`${ROOT}/sales/reps/${enc(range)}/${enc(repCode)}/gaps`, {
  query
}));